import { useMutation } from '@apollo/client';
import {
    CircularProgress,
    IconButton,
    Popper,
    PopperProps,
    Stack,
    styled,
    TextField,
    useTheme,
} from '@mui/material';
import { isEmpty, snakeCase } from 'lodash';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import * as React from 'react';

import { Autocomplete } from './AutoComplete';
import { NestedSubOption } from './types';
import { filter } from './utils';

const StyledChip = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    margin: '0 4px 4px 0',
    borderRadius: '16px',
    padding: '12px 24px',
    display: 'inline-block',
    marginBottom: '8px',
    marginRight: '8px',
    height: 'auto',
    fontSize: '14px',
    fontWeight: 500,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
    },
}));

const EscHint = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '4px 8px',
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontSize: '12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

const EscHintIcon = styled('i')(({ theme }) => ({
    marginRight: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
}));

const PopperWithEscHint = (props: PopperProps) => {
    const { children, ...other } = props;
    return (
        <Popper {...other}>
            <div>
                <EscHint>
                    <EscHintIcon className='fi fi-br-info' />
                    Appuyez sur Esc pour fermer la liste.
                </EscHint>
                {children as React.ReactNode}
            </div>
        </Popper>
    );
};

const ViewListContainer = styled('div')(() => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 4,
    display: 'inline-block',
}));

const FixedItemIcon = styled('div')(({ theme }) => ({
    position: 'absolute',
    left: 8,
    top: 4,
    zIndex: 1,
    '& i': {
        color: theme.palette.primary.main,
        fontSize: 12,
    },
}));

const ViewList = (data: { options: any; value: any }) => {
    const { options, value } = data;
    const theme = useTheme();

    const item = options?.find(option => option.id === value);
    return item ? (
        <ViewListContainer>
            {item?.isFixed && (
                <FixedItemIcon>
                    <i className="fi-sr-star" />
                </FixedItemIcon>
            )}
            <StyledChip
                sx={{
                    backgroundColor: item?.isFixed
                        ? theme.palette.primary.light
                        : theme.palette.background.paper,
                    color: item?.isFixed
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    borderColor: item?.isFixed
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                }}>
                {item?.name}
            </StyledChip>
        </ViewListContainer>
    ) : null;
};

const EndAdornmentContainer = styled(Stack)(({ theme }) => ({
    position: 'absolute',
    right: 0,
    top: '8px',
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
}));

export const AutoCompleteSingleField = React.forwardRef<
    HTMLInputElement,
    {
        value: string | null;
        onChange: (value: string | number | null) => void;
        label: string;
        mutation?: any;
        refetchQueries?: any;
        placeholder?: string;
        disabled?: boolean;
        options?: NestedSubOption[];
        groupBy: (option: NestedSubOption) => string | undefined;
        allowAdd?: boolean;
        viewOnly?: boolean;
        loading?: boolean;
        error?: boolean;
        newValueResolver?: (data: any, dialogValue: NestedSubOption, iconValue?: string) => NestedSubOption;
    }
>((props, ref) => {
    const theme = useTheme();
    // Only use mutation if it's provided
    const [save, { loading: mutationLoading }] = useMutation(props.mutation, {
        refetchQueries: props.refetchQueries,
        awaitRefetchQueries: props.refetchQueries?.length > 0,
    });

    const [isOpen, setIsOpen] = React.useState(false);

    const { showSnackbar } = useSnackbar();

    const [search, setSearch] = React.useState('');

    const [options, setOptions] = React.useState<NestedSubOption[]>([]);
    const [customOptions, setCustomOptions] = React.useState<NestedSubOption[]>([]);

    const selectedValue = React.useMemo(() => {
        if (!props.value) return null;
        return [...options, ...customOptions].find(option => option?.id === props.value) || null;
    }, [props.value, options, customOptions]);

    const { onChange, mutation, label } = props;

    const defaultResolver = (data: any, dialogValue: NestedSubOption, iconValue?: string): NestedSubOption => {
        return {
            name: dialogValue.name,
            id: data._id,
            icon: iconValue,
        };
    }

    const handleAddCustomOption = React.useCallback(
        async (dialogValue: NestedSubOption) => {
            // If no real mutation is provided, we can't add custom options
            if (!mutation) {
                showSnackbar(`Impossible d'ajouter des ${label} personnalisés`, 'warning');
                return;
            }

            let data = null;
            try {
                const { data: resultData } = await save({
                    variables: {
                        name: dialogValue.name,
                    },
                });
                data = resultData?.data;
                showSnackbar(`${label} ajouté avec succès`, 'success');
            } catch (error) {
                console.error('Error adding custom option:', error);
                showSnackbar(`Erreur lors de l'ajout du ${label}`, 'error');
                return;
            }
            if (!data) return;

            const resolver = props.newValueResolver || defaultResolver;
            const newOption = resolver(data, dialogValue, 'shape');

            // For single value, replace the current value
            onChange(newOption.id);

            const newOptions = [
                ...customOptions,
                newOption,
            ];
            setCustomOptions(newOptions);
            setSearch('');
        },
        [onChange, mutation, label, save, showSnackbar, customOptions, setCustomOptions, setSearch, props.newValueResolver],
    );

    React.useEffect(() => {
        setOptions(props.options || []);
    }, [props.options]);

    if (props.viewOnly) {
        return <ViewList options={[...options, ...customOptions]} value={props.value} />;
    }

    return (
        <>
            <Autocomplete
                disablePortal
                PopperComponent={PopperWithEscHint}
                options={[...options, ...customOptions]}
                groupBy={props.groupBy}
                value={selectedValue}
                inputValue={search}
                disabled={props.disabled || mutationLoading}
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                onKeyDown={event => {
                    // escape close the dropdown
                    if (event.key === 'Escape') {
                        // clear search
                        setSearch('');
                        setIsOpen(false);
                    }
                    if (event.key === 'Enter') {
                        if (!props.allowAdd) return;
                        // @ts-ignore Prevent's default 'Enter' behavior.
                        event.defaultMuiPrevented = true;
                        handleAddCustomOption({
                            name: search,
                            id: snakeCase(search),
                        });
                    }
                }}
                onChange={(event, newValue: NestedSubOption | null) => {
                    if (newValue?.inputValue) {
                        // Handle adding custom option
                        handleAddCustomOption({
                            name: newValue.inputValue,
                            id: snakeCase(newValue.inputValue),
                        });
                    } else if (newValue) {
                        // Don't allow removal of fixed single value
                        const currentValue = selectedValue;
                        if (currentValue?.isFixed && !newValue) {
                            // Prevent removal of fixed item
                            return;
                        }
                        props.onChange(newValue.id.toString());
                    } else {
                        props.onChange(null);
                    }
                    setIsOpen(false);
                }}
                onInputChange={(event, newInputValue) => {
                    setSearch(newInputValue);
                }}
                filterOptions={(options: NestedSubOption[], params) => {
                    const filtered = filter(options, params);
                    if (!props.allowAdd) return filtered;
                    if (params.inputValue !== '') {
                        filtered.push({
                            inputValue: params.inputValue,
                            name: `Ajouter "${params.inputValue}"`,
                            id: snakeCase(params.inputValue),
                            parent: { name: 'Autre', id: 'other' },
                        });
                    }
                    return filtered;
                }}
                getOptionLabel={(option: NestedSubOption) => {
                    // e.g. value selected with enter, right from the input
                    if (typeof option === 'string') {
                        return option;
                    }
                    if (option?.inputValue) {
                        return option?.inputValue;
                    }
                    return option?.name || '';
                }}
                getOptionDisabled={(option: NestedSubOption) => option.isFixed}
                isOptionEqualToValue={(option: NestedSubOption, value: NestedSubOption) => option.id === value.id}
                freeSolo
                clearOnBlur
                handleHomeEndKeys
                renderOption={(props, option: NestedSubOption) => (
                    <li {...props}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {option.isFixed && (
                                <IconButton size="small" disabled>
                                    <i 
                                        className="fi-sr-star" 
                                        style={{ color: theme.palette.primary.main }} 
                                    />
                                </IconButton>
                            )}
                            <span>{option.name}</span>
                        </Stack>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={props.placeholder}
                        variant="outlined"
                        sx={{
                            '& .MuiInputBase-root': {
                                rowGap: '8px',
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: props.error ? 'error.main' : 'inherit',
                                opacity: 1,
                            },
                        }}
                        inputRef={ref}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <EndAdornmentContainer
                                    flexDirection={'row'}
                                    alignItems={'center'}>
                                    {!isEmpty(props.value) && !selectedValue?.isFixed && (
                                        <IconButton
                                            size="small"
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                marginRight: 0.5,
                                            }}
                                            onClick={() => {
                                                props.onChange(null);
                                            }}>
                                            <i
                                                className="fi fi-br-cross-small"
                                                style={{ 
                                                    fontSize: 18, 
                                                }}
                                            />
                                        </IconButton>
                                    )}
                                    {mutationLoading ? (
                                        <CircularProgress 
                                            color="inherit" 
                                            size={16} 
                                            sx={{ marginRight: 2 }} 
                                        />
                                    ) : isOpen ? (
                                        <IconButton
                                            size="small"
                                            sx={{
                                                width: 28,
                                                height: 28,
                                            }}
                                            onClick={() => {
                                                setIsOpen(false);
                                            }}>
                                            <i
                                                className="fi-br-check"
                                                style={{ 
                                                    fontSize: 14, 
                                                    color: theme.palette.primary.main 
                                                }}
                                            />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            size="small"
                                            sx={{
                                                width: 28,
                                                height: 28,
                                            }}
                                            onClick={() => {
                                                setIsOpen(true);
                                            }}>
                                            <i
                                                className="fi-br-caret-down"
                                                style={{ 
                                                    fontSize: 16, 
                                                }}
                                            />
                                        </IconButton>
                                    )}
                                </EndAdornmentContainer>
                            ),
                        }}
                    />
                )}
                multiple={false}
                fullWidth
                disableClearable
                disableCloseOnSelect
            />
        </>
    );
});

AutoCompleteSingleField.displayName = 'AutoCompleteSingleField';
